import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import './TauriExportDialog.css';
import { useFilterContext } from '../TauriGlobalContext/TauriGlobalContext';
import { AccessComponentFactory } from '../../visualizations/AccessComponentFactory';
import { Label } from 'reactstrap';
import TauriExportTable from './TauriExportTable/TauriExportTable';
import TauriMultipleTagInput from '../TauriMultipleTagInput/TauriMultipleTagInput';
import config from '../../appURL.json';
import setting from '../../config.json';
import TauriLoader from '../TauriLoader/TauriLoader';

const exportFormats = {
    csv: { key: "csv", name: "Comma Separated Values (CSV)" },
    pdf: { key: "pdf", name: "PDF" }
};

const TauriExportDialog = ({ show, onHide }) => {
    const { filterValue } = useFilterContext();
    const [applications, setApplications] = useState([]);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [selectedApp, setSelectedApp] = useState(null);
    const [exportList, setExportList] = useState(null);
    const [formatDropdownOpen, setFormatDropdownOpen] = useState(false);
    const [selectedFormat, setSelectedFormat] = useState('Select Format');
    const [tags, setTags] = useState([]);
    const [selectedExport, setSelectedExport] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const toggleDropdown = () => setDropdownOpen(!dropdownOpen);
    const toggleFormatDropdown = () => setFormatDropdownOpen(!formatDropdownOpen);

    const validateFields = () => {
        if (!selectedFormat || selectedFormat === 'Select Format') {
            setErrorMessage('Please select a format.');
            return false;
        }
        if (selectedExport.length === 0) {
            setErrorMessage('Please select at least one chart.');
            return false;
        }
        if (tags.length === 0) {
            setErrorMessage('Please enter at least one recipient email.');
            return false;
        }
        setErrorMessage('');
        return true;
    };

    const handleSendToEmail = async () => {
        if (!validateFields()) return;

        setLoading(true); // Show the loader
        const token = localStorage.getItem('token');
        const requestBody = {
            "reference_id": "gio",
            "data": selectedExport,
            "destination": {
                "name": "email",
                "data": {
                    "Format": selectedFormat,
                    "Recipient": tags.toString(),
                    "Remarks": "gio",
                    "includeheaders": "true"
                }
            }
        };

        try {
            const response = await fetch(config.API_ExportData, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(requestBody),
            });

            if (response.ok) {
                onHide(); // Close the modal if the request is successful
            } else {
                console.error("Failed to send email");
                setErrorMessage('Failed to send email. Please try again.');
            }
        } catch (error) {
            console.error("Error sending email", error);
            setErrorMessage('Error sending email. Please try again.');
        } finally {
            setLoading(false); // Hide the loader
        }
    }

    useEffect(() => {
        const fetchApplications = async () => {
            const getPermission = localStorage.getItem('permission');
            const decodedPermission = JSON.parse(atob(getPermission));

            const access = decodedPermission?.DataScope?.Access;

            try {
                const response = await AccessComponentFactory('applicationdashboard', access.Access.toString());
                const apps = response.applications;
                setApplications(apps);

                if (apps.length > 0) {
                    setSelectedApp(apps[0]);
                }
            } catch (error) {
                console.error("Failed to fetch applications", error);
            }
        };

        fetchApplications();
    }, []);

    useEffect(() => {
        const fetchExportList = async () => {
            if (selectedApp) {
                const getPermission = localStorage.getItem('permission');
                const decodedPermission = JSON.parse(atob(getPermission));

                const access = decodedPermission?.DataScope?.Access;

                try {
                    const exportL = await AccessComponentFactory(selectedApp.id.toString(), access.toString());
                    setExportList(exportL.sections);
                } catch (error) {
                    console.error("Failed to fetch export list", error);
                }
            }
        };

        fetchExportList();
    }, [selectedApp]);

    return (
        <Modal show={show} onHide={onHide} className='tauri-exportdialog' centered>
            <Modal.Header className='exportdialog-header'>
                <div className="d-flex flex-column w-100">
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <Label className='exportdialog-titleLbl'>Send to Email</Label>
                    </div>

                    <div className='exportdialog-subheader'>
                        <Label>Dashboard:</Label>
                        <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
                            <DropdownToggle caret>
                                {selectedApp ? selectedApp.name : 'Select Dashboard'}
                            </DropdownToggle>
                            <DropdownMenu className='dropdown-menu dropdown-menu-end'>
                                {applications.map((app) => (
                                    <DropdownItem
                                        key={app.id}
                                        onClick={() => setSelectedApp(app)}
                                    >
                                        {app.name}
                                    </DropdownItem>
                                ))}
                            </DropdownMenu>
                        </Dropdown>
                    </div>
                </div>
            </Modal.Header>
            <Modal.Body className='exportdialog-body'>
                <Label className='tabletitle-label'>Select Chart(s) to Include</Label>
                <TauriExportTable exportList={exportList} selectedExportList={setSelectedExport} />

                <div className='export-format'>
                    <Label>Attached As:</Label>
                    <Dropdown isOpen={formatDropdownOpen} toggle={toggleFormatDropdown} className='ms-2'>
                        <DropdownToggle caret>
                            {selectedFormat !== 'Select Format' ? exportFormats[selectedFormat]?.name : 'Select Format'}
                        </DropdownToggle>
                        <DropdownMenu className='dropdown-menu dropdown-menu-end'>
                            {Object.values(exportFormats).map((format) => (
                                <DropdownItem
                                    key={format.key}
                                    onClick={() => setSelectedFormat(format.key)}
                                >
                                    {format.name}
                                </DropdownItem>
                            ))}
                        </DropdownMenu>
                    </Dropdown>
                </div>

                <div className='export-recipient'>
                    <Label>Email Recipient:</Label>
                    <TauriMultipleTagInput tags={tags} setTags={setTags} />
                </div>
            </Modal.Body>
            <Modal.Footer className='exportdialog-footer'>
                {errorMessage && <div className='error-message-header'>{errorMessage}</div>}
                {loading && <TauriLoader borderSize={'0.1875rem'} widthSize={'1.875rem'} heightSize={'1.875rem'} />}
                <Button
                    className='exportdialog-sendBtn'
                    onClick={handleSendToEmail}
                    disabled={loading}
                >
                    {loading ? 'Sending...' : 'Send to Email'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default TauriExportDialog;